/**
 * "His particulars changed since this draft" (Phase A, A9, 32.7) — on the
 * workbench, above a version made before a correction of his name, ticket,
 * post or section.
 *
 * **Nothing is changed under the officer.** A draft keeps what it said; this
 * says what changed since, and drafting it again uses the corrected values. An
 * issued document stays as issued, and the note says so instead.
 *
 * Whether anything changed is the server's answer (`getParticularsSinceVersion`),
 * not a comparison made here.
 */

import { useQuery } from '@tanstack/react-query'
import { casePageApi } from '../../api/client'
import type { ParticularChangeView } from '../../api/client'
import { readDay } from '../../lib/blocks'
import { readDate } from '../../lib/period'
import './case-page.css'

export function ParticularsChangedNote({
  caseId,
  versionId,
}: {
  caseId: string
  versionId: string
}) {
  const since = useQuery({
    queryKey: ['case', caseId, 'particulars-since', versionId],
    queryFn: () => casePageApi.particularsSinceVersion(caseId, versionId),
  })
  const changes = since.data?.changes ?? []
  if (!since.data || changes.length === 0) return null
  const list = (
    <ul>
      {changes.map((change) => (
        <li key={change.entry_id}>
          <ChangeLine change={change} />
        </li>
      ))}
    </ul>
  )
  if (since.data.issued) {
    return (
      <div className="blocked note particulars-changed" data-testid="particulars-changed">
        <div className="why">
          His particulars were corrected after this was made. It is issued, and stays as issued.
          {list}
        </div>
      </div>
    )
  }
  return (
    <div className="blocked unsettled particulars-changed" data-testid="particulars-changed">
      <div className="why">
        <strong>His particulars changed since this draft.</strong> This draft keeps what it
        said; draft it again to use them.
        {list}
      </div>
    </div>
  )
}

function ChangeLine({ change }: { change: ParticularChangeView }) {
  const was =
    [change.was_value, change.was_english].filter(Boolean).join(' · ') || 'not on the record'
  const now = [change.value, change.english].filter(Boolean).join(' · ') || 'cleared'
  return (
    <>
      {change.label}: <span className="deva">{was}</span> → <span className="deva">{now}</span>{' '}
      <span className="muted">
        — {readDate(readDay(change.at))}
        {change.by ? `, ${change.by}` : ''}: {change.reason}
      </span>
    </>
  )
}
